import React, { Component } from 'react';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from "react-redux";
import { setImage, setData } from '../../../redux/actionCreators';
import Webcam from "react-webcam";
import FooterContainer from '../../../components/footer/FooterContainer';
import PageWithFooter from '../../../components/page_with_footer/PageWithFooter';
import MainButton from '../../../components/buttons/main_button/MainButton';
import DialogModal from '../../../components/dialog_modal/DialogModal';
import { FaExclamationCircle } from "react-icons/fa";
import {
  GestureRecognizer,
  FilesetResolver
} from "@mediapipe/tasks-vision";
import { validateImageQuality } from '@autofact-lib/autofact-lib-check-image-quality';
import config from '../../../config/config';
import '../thumbFingerprintCapture.css';

class MediaPipeThumbFingerprintCapture extends Component {
  constructor(props) {
    super(props);

    // Validation constants
    this.AUTOCAPTURE_GESTURE = "Open_Palm";
    this.AUTOCAPTURE_CONFIDENCE = this.props.stepInfo.autoCaptureConfidence || 0.7;
    this.INSIDE_FRAME_MARGIN = this.props.stepInfo.insideFrameMargin || 0.02;
    this.CENTER_TOLERANCE = this.props.stepInfo.centerTolerance || 0.10;
    this.VIDEO_READY_STATE_HAVE_CURRENT_DATA = 2;
    this.VIDEO_READY_STATE_HAVE_ENOUGH_DATA = 4;
    this.HAND_LANDMARKS = 21;
    this.CAPTURE_VALIDATION_REASON_LANDMARKS = 'landmarks';
    this.CAPTURE_VALIDATION_REASON_GESTURE = 'gesture';
    this.RECOGNIZER_MODE_IMAGE = 'IMAGE';
    this.RECOGNIZER_MODE_VIDEO = 'VIDEO';
    this.FEEDBACK_UPDATE_DURATION = 1200;
    this.RETRY_AUTOCAPTURE_COOLDOWN_MS = 500;
    this.DEFAULT_MAX_FAILED_CAPTURES_BEFORE_MANUAL =
      config.thumbFingerprintMaxFailedAutoCapturesBeforeManual;
    this.CAMERA_FACING_MODES = {
      USER: 'user',
      ENVIRONMENT: 'environment'
    };
    const autoCaptureOnMount = props.stepInfo.isAutoCapture ?? true;
    const defaultAutoFeedback =
      props.stepInfo.feedbackMessage || 'Coloca tu mano en el área de la cámara';
    const defaultManualFeedback =
      props.stepInfo.manualCaptureFeedbackMessage ||
      'Coloca tu mano en el área de la cámara y presiona el botón para capturar';
    this.state = {
      facingMode: props.stepInfo.defaultCamera || this.CAMERA_FACING_MODES.ENVIRONMENT,
      capturedImage: null,
      feedbackMessage: autoCaptureOnMount ? defaultAutoFeedback : defaultManualFeedback,
      iconColor: 'white',
      isGestureRecognizerLoaded: false,
      debugMode: ['true', true, "TRUE"].includes(config.debugMode),
      manualCaptureAfterFailuresActive: false,
      manualCaptureFallbackModalVisible: false,
      manualCaptureFallbackModalMessage: '',
    };

    this.validateImageQuality = props.stepInfo.validateImageQuality ?? true;
    this.isAppleDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    // Refs
    this.webcamRef = React.createRef();
    this.gestureRecognizerRef = React.createRef();
    this.lastVideoTimeRef = React.createRef();
    this.resultsRef = React.createRef();
    this.animationFrameRef = React.createRef();
    this.runningModeRef = React.createRef();
    this.autoCaptureEnabledRef = React.createRef();
    this.isCapturingRef = React.createRef();
    this.isAutoCaptureRef = React.createRef();
    this.retryCooldownRef = React.createRef();
    this.retryCooldownTimeoutRef = React.createRef();
    this.lastFeedbackUpdateRef = React.createRef();
    this.activeStreamRef = React.createRef();

    // Initialize refs
    this.lastVideoTimeRef.current = -1;
    this.resultsRef.current = undefined;
    this.animationFrameRef.current = null;
    this.runningModeRef.current = this.RECOGNIZER_MODE_VIDEO;
    this.gestureRecognizerRef.current = null;
    this.autoCaptureEnabledRef.current = props.stepInfo.isAutoCapture ?? true;
    this.failedCaptureValidationCount = 0;
    this.isCapturingRef.current = false;
    this.isAutoCaptureRef.current = false;
    this.retryCooldownRef.current = false;
    this.retryCooldownTimeoutRef.current = null;
    this.lastFeedbackUpdateRef.current = 0;
    this.activeStreamRef.current = null; // [MEJORA 2]

    // Bind methods
    this.captureImage = this.captureImage.bind(this);
    this.retry = this.retry.bind(this);
    this.setImageInternal = this.setImageInternal.bind(this);
    this.predictWebcam = this.predictWebcam.bind(this);
    this.isLandmarkInFrame = this.isLandmarkInFrame.bind(this);
    this.analyzeLandmarkVisibility = this.analyzeLandmarkVisibility.bind(this);
    this.isHandCentered = this.isHandCentered.bind(this);
    this.handleVideoLoaded = this.handleVideoLoaded.bind(this);
    this.toggleCamera = this.toggleCamera.bind(this);
    this.checkCapturedImageLandmarks = this.checkCapturedImageLandmarks.bind(this);
    this.allLandmarksVisible = this.allLandmarksVisible.bind(this);
    this.restartImageCapture = this.restartImageCapture.bind(this);
  }

  writeLog = (debugMode, ...log) => {
    if (debugMode) {
      console.log(...log);
    }
  };

  closeManualCaptureFallbackModal = () => {
    this.setState({
      manualCaptureFallbackModalVisible: false,
      manualCaptureFallbackModalMessage: '',
    });
  };

  handlePostCaptureValidationFailure() {
    if (!this.autoCaptureEnabledRef.current) {
      return;
    }
    const max =
      this.props.stepInfo.maxFailedCapturesBeforeManual ??
      this.DEFAULT_MAX_FAILED_CAPTURES_BEFORE_MANUAL;
    if (typeof max !== 'number' || max <= 0) {
      return;
    }
    this.failedCaptureValidationCount += 1;
    if (this.failedCaptureValidationCount < max) {
      return;
    }
    this.autoCaptureEnabledRef.current = false;
    const defaultMsg =
      `La captura automática falló tras ${max} intentos. Realiza la captura de forma manual usando el botón.`;
    const msg =
      this.props.stepInfo.manualCaptureAfterFailuresMessage || defaultMsg;
    const manualIdleFeedback =
      this.props.stepInfo.manualCaptureFeedbackMessage ||
      'Coloca tu mano en el área de la cámara y presiona el botón para capturar';
    this.lastFeedbackUpdateRef.current = Date.now();
    this.setState({
      manualCaptureAfterFailuresActive: true,
      manualCaptureFallbackModalVisible: true,
      manualCaptureFallbackModalMessage: msg,
      iconColor: 'white',
      feedbackMessage: manualIdleFeedback,
    });
  }

  isLandmarkInFrame(landmark) {
    if (!landmark || typeof landmark.x !== 'number' || typeof landmark.y !== 'number') {
      return false;
    }
    const isXInFrame = this.INSIDE_FRAME_MARGIN < landmark.x && landmark.x < (1 - this.INSIDE_FRAME_MARGIN);
    const isYInFrame = this.INSIDE_FRAME_MARGIN < landmark.y && landmark.y < (1 - this.INSIDE_FRAME_MARGIN);
    return isXInFrame && isYInFrame;
  }

  allLandmarksVisible(visibilityInfo) {
    return visibilityInfo !== null && visibilityInfo.total === this.HAND_LANDMARKS && visibilityInfo.visible === this.HAND_LANDMARKS;
  }

  analyzeLandmarkVisibility(landmarks) {
    if (!landmarks || landmarks.length === 0) return null;
    const visibleLandmarks = landmarks.filter((landmark) =>
      this.isLandmarkInFrame(landmark)
    );
    return {
      total: landmarks.length,
      visible: visibleLandmarks.length,
      hidden: landmarks.length - visibleLandmarks.length,
      visibilityRate: (visibleLandmarks.length / landmarks.length * 100).toFixed(1),
    };
  }


  async checkCapturedImageLandmarks(img) {
    const recognizer = this.gestureRecognizerRef.current;
    if (!recognizer) {
      return { ok: false, reason: this.CAPTURE_VALIDATION_REASON_LANDMARKS };
    }
    try {
      if (this.runningModeRef.current !== this.RECOGNIZER_MODE_IMAGE) {
        await recognizer.setOptions({ runningMode: this.RECOGNIZER_MODE_IMAGE });
        this.runningModeRef.current = this.RECOGNIZER_MODE_IMAGE;
      }

      const imgAnalysis = recognizer.recognize(img);
      if (!imgAnalysis?.landmarks?.length) {
        this.writeLog(this.state.debugMode, "No landmarks found in image");
        return { ok: false, reason: this.CAPTURE_VALIDATION_REASON_LANDMARKS };
      }
      const visibilityInfo = this.analyzeLandmarkVisibility(imgAnalysis.landmarks[0]);
      this.writeLog(this.state.debugMode, "Landmarks visibility info", visibilityInfo);
      if (!this.allLandmarksVisible(visibilityInfo)) {
        return { ok: false, reason: this.CAPTURE_VALIDATION_REASON_LANDMARKS };
      }

      const topGesture = imgAnalysis.gestures?.[0]?.[0];
      const confidence = typeof topGesture?.score === 'number'
        ? topGesture.score
        : parseFloat(topGesture?.score);
      if (
        !topGesture ||
        topGesture.categoryName !== this.AUTOCAPTURE_GESTURE ||
        Number.isNaN(confidence) ||
        confidence < this.AUTOCAPTURE_CONFIDENCE
      ) {
        this.writeLog(this.state.debugMode, "Captured image gesture check failed", topGesture);
        return { ok: false, reason: this.CAPTURE_VALIDATION_REASON_GESTURE };
      }

      return { ok: true };
    } catch (e) {
      this.writeLog(this.state.debugMode, "Landmarks analysis error", e);
      return { ok: false, reason: this.CAPTURE_VALIDATION_REASON_LANDMARKS };
    } finally {
      await recognizer.setOptions({ runningMode: this.RECOGNIZER_MODE_VIDEO });
      this.runningModeRef.current = this.RECOGNIZER_MODE_VIDEO;
    }
  }

  isHandCentered(landmarks) {
    if (!landmarks || landmarks.length === 0) return false;
    let sumX = 0;
    let sumY = 0;
    let validLandmarks = 0;
    for (const landmark of landmarks) {
      if (typeof landmark.x === 'number' && typeof landmark.y === 'number') {
        sumX += landmark.x;
        sumY += landmark.y;
        validLandmarks++;
      }
    }
    if (validLandmarks === 0) return false;
    const centerX = sumX / validLandmarks;
    const centerY = sumY / validLandmarks;
    const isXCentered = Math.abs(centerX - 0.5) <= this.CENTER_TOLERANCE;
    const isYCentered = Math.abs(centerY - 0.5) <= this.CENTER_TOLERANCE;
    return isXCentered && isYCentered;
  }

  restartImageCapture() {
    this.isCapturingRef.current = false;
    this.isAutoCaptureRef.current = false;
    const videoElement = this.webcamRef.current?.video;
    if (videoElement && videoElement.readyState === this.VIDEO_READY_STATE_HAVE_ENOUGH_DATA && !this.state.capturedImage) {
      this.predictWebcam();
    }
  }

  async componentDidMount() {
    window.scrollTo(0, document.body.scrollHeight);
    await this.createGestureRecognizer();
    await this.getMediaDevices();
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.isGestureRecognizerLoaded && this.state.isGestureRecognizerLoaded) {
      const videoElement = this.webcamRef.current?.video;
      if (videoElement && videoElement.readyState === this.VIDEO_READY_STATE_HAVE_ENOUGH_DATA && !this.state.capturedImage) {
        this.predictWebcam();
      }
    }
  }

  async createGestureRecognizer() {
    if (this.gestureRecognizerRef.current) {
      return;
    }
    try {
      const vision = await FilesetResolver.forVisionTasks("/mediapipe/wasm");
      const recognizer = await GestureRecognizer.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: '/mediapipe/models/gesture_recognizer.task',
          delegate: 'GPU'
        },
        runningMode: this.RECOGNIZER_MODE_VIDEO,
      });
      this.gestureRecognizerRef.current = recognizer;
      if (!this.state.isGestureRecognizerLoaded) {
        this.setState({ isGestureRecognizerLoaded: true });
      }
    } catch (error) {
      this.updateFeedback('Ocurrió un error, intente nuevamente.', 'red');
    }
  }

  componentWillUnmount() {
    if (this.animationFrameRef.current !== null) {
      cancelAnimationFrame(this.animationFrameRef.current);
      this.animationFrameRef.current = null;
    }
    if (this.retryCooldownTimeoutRef.current !== null) {
      clearTimeout(this.retryCooldownTimeoutRef.current);
      this.retryCooldownTimeoutRef.current = null;
    }
    if (this.gestureRecognizerRef.current) {
      this.gestureRecognizerRef.current.close();
      this.gestureRecognizerRef.current = null;
    }
    if (this.webcamRef.current) {
      const videoElement = this.webcamRef.current.video;
      if (videoElement && this.handleVideoLoaded) {
        videoElement.removeEventListener("loadeddata", this.handleVideoLoaded);
      }
      this.stopCameraStream();
    }
  }

  handleVideoLoaded() {
    if (this.state.isGestureRecognizerLoaded && this.gestureRecognizerRef.current && !this.state.capturedImage) {
      this.predictWebcam();
    }
  }

  hasLiveVideoStream() {
    const video = this.webcamRef.current?.video;
    const stream = video?.srcObject;
    if (!stream || typeof stream.getVideoTracks !== 'function') {
      return false;
    }
    return stream.getVideoTracks().some((t) => t.readyState === 'live');
  }

  async getMediaDevices() {
    try {
      if (this.animationFrameRef.current !== null) {
        cancelAnimationFrame(this.animationFrameRef.current);
        this.animationFrameRef.current = null;
      }

      this.stopCameraStream();

      const videoConstraints = {
        facingMode: this.state.facingMode,
        aspectRatio: 1,
        width: { ideal: 1920 },
        height: { ideal: 1920 }
      };

      const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints });

      this.activeStreamRef.current = stream;

      if (this.webcamRef.current) {
        const videoElement = this.webcamRef.current.video;
        if (videoElement) {
          videoElement.removeEventListener("loadeddata", this.handleVideoLoaded);
          videoElement.srcObject = stream;
          videoElement.addEventListener("loadeddata", this.handleVideoLoaded);
          if (
            videoElement.readyState >= this.VIDEO_READY_STATE_HAVE_CURRENT_DATA &&
            this.state.isGestureRecognizerLoaded &&
            this.gestureRecognizerRef.current &&
            !this.state.capturedImage
          ) {
            this.predictWebcam();
          }
        }
      }
    } catch (error) {
      this.updateFeedback('Error al acceder a la cámara. Por favor, verifica los permisos.', 'red');
    }
  }

  async predictWebcam() {
    if (this.state.capturedImage) {
      return;
    }

    const videoElement = this.webcamRef.current?.video;
    const recognizer = this.gestureRecognizerRef.current;

    if (!videoElement || !recognizer || videoElement.readyState !== this.VIDEO_READY_STATE_HAVE_ENOUGH_DATA) {
      return;
    }

    if (this.runningModeRef.current === this.RECOGNIZER_MODE_IMAGE) {
      this.runningModeRef.current = this.RECOGNIZER_MODE_VIDEO;
      await recognizer.setOptions({ runningMode: this.RECOGNIZER_MODE_VIDEO });
    }

    let nowInMs = Date.now();
    if (videoElement.currentTime !== this.lastVideoTimeRef.current) {
      this.lastVideoTimeRef.current = videoElement.currentTime;
      this.resultsRef.current = recognizer.recognizeForVideo(videoElement, nowInMs);
    }

    if (this.retryCooldownRef.current) {
      if (!this.state.capturedImage && videoElement && videoElement.readyState === this.VIDEO_READY_STATE_HAVE_ENOUGH_DATA) {
        this.animationFrameRef.current = requestAnimationFrame(() => this.predictWebcam());
      }
      return;
    }

    if (this.resultsRef.current?.gestures && this.resultsRef.current.gestures.length > 0) {
      const categoryName = this.resultsRef.current.gestures[0][0].categoryName;
      const categoryScore = parseFloat(
        (this.resultsRef.current.gestures[0][0].score).toString()
      ).toFixed(2);
      const confidence = parseFloat(categoryScore);
      let allLandmarksVisible = false;
      let handIsCentered = false;

      if (this.resultsRef.current.landmarks && this.resultsRef.current.landmarks.length > 0) {
        const visibilityInfo = this.analyzeLandmarkVisibility(this.resultsRef.current.landmarks[0]);
        allLandmarksVisible = this.allLandmarksVisible(visibilityInfo);
        handIsCentered = this.isHandCentered(this.resultsRef.current.landmarks[0]);
      }

      if (categoryName === this.AUTOCAPTURE_GESTURE) {
        if (!handIsCentered) {
          this.updateFeedback('Centra tu mano en la pantalla', 'white');
        } else if (!allLandmarksVisible) {
          this.updateFeedback('Asegúrate de que toda la mano sea visible', 'white');
        } else if (confidence < this.AUTOCAPTURE_CONFIDENCE) {
          this.updateFeedback('Mantén la palma abierta', 'white');
        } else {
          if (
            this.autoCaptureEnabledRef.current &&
            !this.isCapturingRef.current &&
            !this.retryCooldownRef.current
          ) {
            this.updateFeedback('Perfecto. Mantén la palma quieta; capturando…', 'white');
            this.isCapturingRef.current = true;
            this.isAutoCaptureRef.current = true;
            setTimeout(() => {
              if (!this.state.capturedImage) {
                this.captureImage();
              }
              this.isCapturingRef.current = false;
            }, 800);
          } else if (this.isCapturingRef.current) {
            this.updateFeedback('Perfecto. Mantén la palma quieta; capturando…', 'white');
          } else {
            this.updateFeedback('Muy bien. Cuando estés listo, pulsa el botón de captura.', 'white');
          }
        }
      } else {
        this.updateFeedback('Abre la palma de tu mano', 'white');
      }
    } else {
      const initialMessage = this.autoCaptureEnabledRef.current
        ? (this.props.stepInfo.feedbackMessage || 'Coloca tu mano en el centro de la cámara')
        : (this.props.stepInfo.manualCaptureFeedbackMessage ||
          'Coloca tu mano en el área de la cámara y presiona el botón para capturar');
      this.updateFeedback(initialMessage, 'white');
    }

    if (!this.state.capturedImage && videoElement && videoElement.readyState === this.VIDEO_READY_STATE_HAVE_ENOUGH_DATA) {
      this.animationFrameRef.current = requestAnimationFrame(() => this.predictWebcam());
    }
  }

  updateFeedback(message, color, isPriority = false) {
    if (this.state.feedbackMessage === message && this.state.iconColor === color) {
      return;
    }
    const now = Date.now();
    if (!isPriority && (now - this.lastFeedbackUpdateRef.current) < this.FEEDBACK_UPDATE_DURATION) {
      return;
    }
    this.lastFeedbackUpdateRef.current = now;
    this.setState({ feedbackMessage: message, iconColor: color });
  }

  stopCameraStream() {
    const stream = this.activeStreamRef?.current;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      this.activeStreamRef.current = null;
    }
    const videoElement = this.webcamRef.current?.video;
    if (videoElement) {
      videoElement.srcObject = null;
    }
  }

  finalizeCapture(imageSrc, data, isAutoCapture) {
    this.failedCaptureValidationCount = 0;
    this.setImageInternal(imageSrc);
    this.props.setData(data);
    if (this.animationFrameRef.current !== null) {
      cancelAnimationFrame(this.animationFrameRef.current);
      this.animationFrameRef.current = null;
    }
    if (isAutoCapture) {
      this.isAutoCaptureRef.current = false;
    }
    this.setState({ capturedImage: imageSrc });
  }

  async checkCapturedImageQuality(img) {
    const qualityResult = await validateImageQuality(
      img,
      { customThresholds: config.fingerprintCustomThresholds, debugMode: this.state.debugMode },
      'front'
    );

    let response = { isValid: false, result: qualityResult };
    let feedbackIsPriority = true;

    if (!qualityResult.brightness) {
      this.updateFeedback('Hay poca luz. Por favor, aumenta la iluminación', 'red', feedbackIsPriority);
      return response;
    }
    if (!qualityResult.contrast) {
      this.updateFeedback('Hay demasiada luz. Redúcela para equilibrar el color y contraste', 'red', feedbackIsPriority);
      return response;
    }
    if (!qualityResult.sharpness) {
      this.updateFeedback('La imagen está borrosa. Ajusta la distancia para enfocar mejor', 'yellow', feedbackIsPriority);
      return response;
    }
    response.isValid = true;
    return response;
  }

  createCaptureData(qualityResult = null) {
    const baseData = {
      provider: 'mediapipe',
      timestamp: new Date().toISOString(),
      validateImageQuality: this.validateImageQuality,
      isAppleDevice: this.isAppleDevice,
    };
    if (qualityResult) {
      return { ...baseData, ...qualityResult };
    }
    return baseData;
  }

  captureImage() {
    if (this.animationFrameRef.current !== null) {
      cancelAnimationFrame(this.animationFrameRef.current);
      this.animationFrameRef.current = null;
    }

    const isAutoCapture = this.isAutoCaptureRef.current;

    const videoElement = this.webcamRef.current?.video;
    if (videoElement && videoElement.readyState >= this.VIDEO_READY_STATE_HAVE_CURRENT_DATA) {
      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

      const imageSrc = canvas.toDataURL('image/jpeg', 1.0);
      const img = new Image();
      img.src = imageSrc;

      img.onload = async () => {
        let feedbackIsPriority = true;

        const captureValidation = await this.checkCapturedImageLandmarks(img);
        if (!captureValidation.ok) {
          const message = captureValidation.reason === this.CAPTURE_VALIDATION_REASON_GESTURE
            ? 'Mantén la palma abierta frente a la cámara'
            : 'La mano completa debe estar visible';
          this.updateFeedback(message, 'red', feedbackIsPriority);
          this.writeLog(this.state.debugMode, 'Capture validation failed, restarting', captureValidation.reason);
          if (this.autoCaptureEnabledRef.current) {
            this.handlePostCaptureValidationFailure();
          }
          this.restartImageCapture();
          return;
        }

        let data;
        if (this.validateImageQuality) {
          const { isValid, qualityResult } = await this.checkCapturedImageQuality(img);
          this.writeLog(this.state.debugMode, 'Image quality check - ', 'is valid: ', isValid, 'result: ', qualityResult);
          if (!isValid) {
            this.writeLog(this.state.debugMode, 'invalid quality, restarting capture');
            if (this.autoCaptureEnabledRef.current) {
              this.handlePostCaptureValidationFailure();
            }
            this.restartImageCapture();
            return;
          }
          this.updateFeedback('¡Perfecto! Capturando...', 'green');
          data = this.createCaptureData(qualityResult);
        } else {
          data = this.createCaptureData();
        }

        this.finalizeCapture(imageSrc, data, isAutoCapture);
      };

      img.onerror = () => {
        const data = this.createCaptureData();
        this.finalizeCapture(imageSrc, data, isAutoCapture);
      };
    }
  }

  setImageInternal = (image) => {
    try {
      this.props.setImage(image);
    } catch (error) {
      console.error("Error saving image:", error);
    }
  }

  async retry() {
    if (this.retryCooldownTimeoutRef.current !== null) {
      clearTimeout(this.retryCooldownTimeoutRef.current);
      this.retryCooldownTimeoutRef.current = null;
    }
    if (this.animationFrameRef.current !== null) {
      cancelAnimationFrame(this.animationFrameRef.current);
      this.animationFrameRef.current = null;
    }

    this.isCapturingRef.current = false;
    this.isAutoCaptureRef.current = false;
    this.resultsRef.current = undefined;
    this.lastVideoTimeRef.current = -1;
    this.retryCooldownRef.current = true;
    this.failedCaptureValidationCount = 0;
    this.autoCaptureEnabledRef.current = this.props.stepInfo.isAutoCapture ?? true;

    const initialMessage = this.props.stepInfo.feedbackMessage ||
      'Coloca tu mano en el área de la cámara y presiona el botón para capturar';

    const needsUpdate = this.state.capturedImage !== null ||
      this.state.feedbackMessage !== initialMessage ||
      this.state.iconColor !== 'white' ||
      this.state.manualCaptureAfterFailuresActive;

    const executeRetry = async () => {
      if (!this.gestureRecognizerRef.current) {
        await this.createGestureRecognizer();
      }
      if (!this.hasLiveVideoStream()) {
        await this.getMediaDevices();
      } else {
        const videoElement = this.webcamRef.current?.video;
        if (
          videoElement &&
          videoElement.readyState >= this.VIDEO_READY_STATE_HAVE_ENOUGH_DATA &&
          this.state.isGestureRecognizerLoaded &&
          this.gestureRecognizerRef.current &&
          !this.state.capturedImage
        ) {
          this.predictWebcam();
        }
      }
      this.retryCooldownTimeoutRef.current = setTimeout(() => {
        this.retryCooldownRef.current = false;
        this.retryCooldownTimeoutRef.current = null;
      }, this.RETRY_AUTOCAPTURE_COOLDOWN_MS);
    };

    if (needsUpdate) {
      this.lastFeedbackUpdateRef.current = Date.now();
      this.setState({
        capturedImage: null,
        feedbackMessage: initialMessage,
        iconColor: 'white',
        manualCaptureAfterFailuresActive: false,
        manualCaptureFallbackModalVisible: false,
        manualCaptureFallbackModalMessage: '',
      }, executeRetry);
    } else {
      this.setState({
        manualCaptureAfterFailuresActive: false,
        manualCaptureFallbackModalVisible: false,
        manualCaptureFallbackModalMessage: '',
      }, executeRetry);
    }
  }

  async toggleCamera() {
    if (this.webcamRef.current?.video?.srcObject) {
      const stream = this.webcamRef.current.video.srcObject;
      stream.getTracks().forEach(track => track.stop());
    }
    this.setState(prevState => ({
      facingMode: prevState.facingMode === this.CAMERA_FACING_MODES.USER
        ? this.CAMERA_FACING_MODES.ENVIRONMENT
        : this.CAMERA_FACING_MODES.USER
    }), async () => {
      await this.getMediaDevices();
    });
  }

  render() {
    const videoConstraints = {
      facingMode: { ideal: this.state.facingMode },
      aspectRatio: 1,
      autoGainControl: true,
      audio: false,
      width: { ideal: 1920 },
      height: { ideal: 1920 }
    };
    const ToggleCameraIcon = require('../../../resources/images/turncamera.svg').default;

    const manualFallbackTitle =
      this.props.stepInfo.manualCaptureAfterFailuresModalTitle || 'Fallo en captura automática';

    return (
      <>
        <DialogModal
          isVisible={this.state.manualCaptureFallbackModalVisible}
          onClose={this.closeManualCaptureFallbackModal}
          title={manualFallbackTitle}
          principalMessage={this.state.manualCaptureFallbackModalMessage}
          type="camera"
        />
        <div className="thumb-fingerprint-capture page-layout">
          <div
            className={
              this.state.capturedImage
                ? 'thumb-live-layer thumb-live-layer--hidden-for-preview'
                : 'thumb-live-layer'
            }
          >
            <Webcam
              audio={false}
              ref={this.webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              className={'thumb-webcam'}
              playsInline
            />
          </div>
          {!this.state.capturedImage && (
            <>
              <div className={`thumb-feedback-message thumb-feedback iconColor-${this.state.iconColor}`}>
                <FaExclamationCircle className="thumb-feedback-icon" />
                <span className="thumb-feedback-text">{this.state.feedbackMessage}</span>
              </div>
              <div className="button-container-thumb">
                {(!this.autoCaptureEnabledRef.current || this.state.manualCaptureAfterFailuresActive) && (
                  <Button
                    className={'capture-button thumb-capture-btn'}
                    onClick={this.captureImage}
                  />
                )}
                <div className={'toggle-camera-button'} onClick={this.toggleCamera}>
                  <img src={ToggleCameraIcon} alt="Toggle camera" />
                </div>
              </div>
            </>
          )}
          {this.state.capturedImage && (
            <div className="thumb-preview">
              <PageWithFooter
                footer={(
                  <FooterContainer>
                    <Col xs={6}>
                      <div className="camera-button">
                        <MainButton
                          onClick={this.retry}
                          text="Repetir"
                          outline={true}
                        />
                      </div>
                    </Col>
                    <Col xs={6}>
                      <MainButton
                        onClick={this.props.nextStep}
                        className="next-button"
                      >
                        {this.props.stepInfo.buttonText || 'Continuar'}
                      </MainButton>
                    </Col>
                  </FooterContainer>
                )}
              >
                <Grid>
                  <div className="content">
                    <Row>
                      <Col xs={12} className="content-image">
                        <p className="content-description">
                          Asegúrate de que la captura cumpla con lo siguiente para evitar rechazos:
                        </p>
                        <ul>
                          <li>Es tu palma (no el dorso)</li>
                          <li>La imagen es nítida y enfocada</li>
                          <li>La palma está completa dentro del recuadro</li>
                        </ul>
                        <img src={this.state.capturedImage} alt="Huella digital capturada" />
                      </Col>
                    </Row>
                  </div>
                </Grid>
              </PageWithFooter>
            </div>
          )}

        </div>
      </>
    );
  }
}

const mapStateToProps = state => {
  return {
    stepInfo: state.stepInfo
  };
};

const mapDispatchToProps = dispatch => {
  return {
    setImage(image) {
      dispatch(setImage(image));
    },
    setData: (data) => {
      dispatch(setData(data));
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MediaPipeThumbFingerprintCapture);
